import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DataGridPro, GridActionsCellItem, koKR } from '@mui/x-data-grid-pro';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Send } from '@mui/icons-material';

import { ConfirmDialog } from '../dialog';
import { CustomNoRowsOverlay } from '../datagrid';
import { dateFormat } from '../../utils';
import { getGridHeight } from '../../constants/gridHeights';
import * as subMatTemplateActions from '../../store/subMatTemplate';
import * as subMatFolderActions from '../../store/subMatFolder';
import SubMatTemplateEditDialog from './SubMatTemplateEditDialog';
import SubMatTemplateSendDialog from './SubMatTemplateSendDialog';

const GRID_HEIGHT = getGridHeight('ONE_LINE_LAYER', 54);
const theme = createTheme();
/** 템플릿 수정 창 하단 버튼과 같은 높이. 데스크톱 16px/패딩 6, 모바일 슬롯 14px/패딩 6 */
const REGISTER_BUTTON_SX = {
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: 1.75,
  padding: '6px 16px',
  minHeight: 40,
  height: 40,
  boxSizing: 'border-box',
};
const REGISTER_BUTTON_SX_MOBILE = {
  ...REGISTER_BUTTON_SX,
  fontSize: '14px',
  minHeight: 36.5,
  height: 36.5,
};

const G04docuSubMatTemplateManagement = () => {
  const dispatch = useDispatch();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const rows = useSelector((s) => s.subMatTemplate.subMatTemplates);
  const rowCount = useSelector((s) => s.subMatTemplate.count);
  const selected = useSelector((s) => s.subMatTemplate.subMatTemplate);
  const folders = useSelector((s) => s.subMatFolder.subMatFolders);

  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [crudMode, setCrudMode] = useState('C');
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTemplates, setSendTemplates] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removeId, setRemoveId] = useState(null);
  const [removeName, setRemoveName] = useState('');
  const [errors, setErrors] = useState([]);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);

  const loadAll = async () => {
    setLoaded(false);
    await dispatch(subMatTemplateActions.selectAll());
    await dispatch(subMatFolderActions.selectTree());
    setLoaded(true);
  };

  useEffect(() => {
    loadAll();
  }, [dispatch]);

  const openCreate = () => {
    setCrudMode('C');
    setOpen(true);
  };

  const openEdit = async (id) => {
    setErrors([]);
    setOpenBackdrop(true);
    try {
      await dispatch(subMatTemplateActions.select(id));
      setCrudMode('U');
      setOpen(true);
    } catch (_err) {
      setErrors(['템플릿을 불러올 수 없습니다.']);
    }
    setOpenBackdrop(false);
  };

  const openSend = async (ids) => {
    const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
    if (!list.length) return;
    setErrors([]);
    setOpenBackdrop(true);
    try {
      const loaded = [];
      for (let i = 0; i < list.length; i += 1) {
        loaded.push(await dispatch(subMatTemplateActions.select(list[i])));
      }
      setSendTemplates(loaded.filter(Boolean));
      setSendOpen(true);
    } catch (_err) {
      setErrors(['템플릿을 불러올 수 없습니다.']);
    }
    setOpenBackdrop(false);
  };

  const requestDelete = (row) => {
    setRemoveId(row.id);
    setRemoveName(row.name || '');
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!removeId) return;
    try {
      await dispatch(subMatTemplateActions.remove(removeId));
      await loadAll();
    } catch (_err) {
      setErrors(['삭제에 실패했습니다.']);
    }
    setConfirmOpen(false);
    setRemoveId(null);
    setRemoveName('');
  };

  const columns = [
    {
      field: 'name',
      headerName: '템플릿 이름',
      flex: 1,
      minWidth: 220,
    },
    {
      field: 'memo',
      headerName: '메모',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'itemCount',
      headerName: '항목 수',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (p) => p.value ?? 0,
    },
    {
      field: 'updatedAt',
      headerName: '수정일시',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (p) => dateFormat(p.value),
    },
    {
      field: 'createdAt',
      headerName: '생성일시',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (p) => dateFormat(p.value),
    },
    {
      field: 'actions',
      headerName: (
        <Tooltip title="수정/삭제/메일전송">
          <Box>기능</Box>
        </Tooltip>
      ),
      width: 70,
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<Edit />}
          label="수정"
          onClick={() => openEdit(params.id)}
          showInMenu
        />,
        <GridActionsCellItem
          key="delete"
          icon={<Delete />}
          label="삭제"
          onClick={() => requestDelete(params.row)}
          showInMenu
        />,
        <GridActionsCellItem
          key="send"
          icon={<Send />}
          label="메일전송"
          onClick={() => openSend(params.id)}
          showInMenu
        />,
      ],
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Backdrop sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }} open={openBackdrop}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container component="main" maxWidth="false">
        <CssBaseline />
        {errors.length > 0 && (
          <Box sx={{ color: 'error.main', mt: 1 }}>
            {errors.map((e, i) => <div key={i}>{e}</div>)}
          </Box>
        )}
        <Box sx={{ mt: 3 }}>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate} fullWidth sx={REGISTER_BUTTON_SX_MOBILE}>
                등록하기
              </Button>
              {!loaded && <LinearProgress />}
              {loaded && rows.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  템플릿이 없습니다.
                </Typography>
              )}
              <List disablePadding>
                {rows.map((row) => (
                  <Paper key={row.id} variant="outlined" sx={{ mb: 1 }}>
                    <ListItemButton onClick={() => openEdit(row.id)} sx={{ alignItems: 'flex-start', py: 1.25 }}>
                      <ListItemText
                        primary={row.name || '(이름 없음)'}
                        secondary={dateFormat(row.updatedAt) || ''}
                        primaryTypographyProps={{ noWrap: true }}
                      />
                      <IconButton
                        aria-label="메일전송"
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          openSend(row.id);
                        }}
                      >
                        <Send fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="삭제"
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          requestDelete(row);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  </Paper>
                ))}
              </List>
            </Box>
          ) : (
          <>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
              <Button
                variant="outlined"
                startIcon={<Send />}
                disabled={selectionModel.length === 0}
                onClick={() => openSend(selectionModel)}
              >
                메일 보내기
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={REGISTER_BUTTON_SX}>
                등록하기
              </Button>
            </Grid>
          </Grid>
          <div style={{ height: GRID_HEIGHT.REDUCED, width: '100%' }}>
            <DataGridPro
              localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
              sx={{ cursor: 'pointer', fontSize: '0.85em' }}
              initialState={{ pinnedColumns: { right: ['actions'] } }}
              slots={{ noRowsOverlay: CustomNoRowsOverlay, loadingOverlay: LinearProgress }}
              headerHeight={38}
              rowHeight={34}
              loading={!loaded}
              rows={rows}
              columns={columns}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              rowsPerPageOptions={[10, 20, 50, 100]}
              onPageChange={setPage}
              page={page}
              rowCount={rowCount}
              pagination
              paginationMode="client"
              checkboxSelection
              disableSelectionOnClick
              selectionModel={selectionModel}
              onSelectionModelChange={(ids) => setSelectionModel(ids)}
              onRowDoubleClick={(p) => openEdit(p.id)}
            />
          </div>
          </>
          )}
        </Box>

        <SubMatTemplateEditDialog
          open={open}
          onClose={() => setOpen(false)}
          crudMode={crudMode}
          template={crudMode === 'C' ? null : selected}
          folders={folders}
          onSaved={loadAll}
        />

        <SubMatTemplateSendDialog
          open={sendOpen}
          onClose={() => {
            setSendOpen(false);
            setSendTemplates([]);
          }}
          templates={sendTemplates}
        />

        <ConfirmDialog
          removeId={removeId}
          title="삭제"
          open={confirmOpen}
          setOpen={setConfirmOpen}
          onConfirm={handleDelete}
          onCancel={() => {}}
        >
          {`"${removeName}" 템플릿을 삭제하시겠습니까?`}
        </ConfirmDialog>
      </Container>
    </ThemeProvider>
  );
};

export default G04docuSubMatTemplateManagement;
